/**
 * Integrytas Group - Common Security Module (CSM)
 * ImportEngine — generic CSV/XLSX import wizard.
 *
 * Replaces the monolithic BulkImportPage.  Any screen can embed it:
 *
 *   <ImportEngine
 *     session={session}
 *     // Optional: lock to a specific table
 *     initialTableName="csm_user_master"
 *     // Optional: provide explicit column schema instead of loading from meta
 *     schemaColumns={[{ key: 'email', label: 'Email', required: true, dataType: 'string' }]}
 *     // Optional: custom validators per column (run before Common Routine)
 *     validators={{ email: (v) => /^.+@.+/.test(String(v)) ? null : 'Invalid email' }}
 *     onComplete={(results) => notify('SUCCESS', 'Import done')}
 *   />
 *
 * Pipeline:
 *   1  Upload  →  Parse (Papa/ExcelJS)
 *   2  Map     →  file columns → API fields
 *   3  Validate →  client-side schema rules + custom validators
 *   4  Commit  →  executeCommonRoutine() per valid row; invalid rows skip
 *   5  Results →  per-row outcome grid with field-level error details
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Papa from 'papaparse';
import ExcelJS from 'exceljs';
import {
  executeCommonRoutine,
  loadCommonRoutineMeta,
  type CommonRoutineResult,
  type CommonRoutineTableMeta,
  type OnMissingLookup,
} from '../../lib/common_routine';
import { getForensicActorId, type AuthSession } from '../../types/auth';
import { FilteredModalSingleSelect } from './FilteredModalSingleSelect';
import type { MultiSelectOption } from './FilteredModalMultiSelect';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ImportDataType = 'string' | 'number' | 'date' | 'boolean';

export interface ImportColumnSchema {
  key:       string;
  label:     string;
  required?: boolean;
  dataType?: ImportDataType;
  maxLength?: number;
  /** Return an error string if invalid, null if valid. */
  validate?: (value: unknown, row: Record<string, unknown>) => string | null;
}

export interface ImportRowError {
  rowIndex:  number;
  field:     string;
  code:      string;
  message:   string;
  source:    'client' | 'server';
}

export interface ImportResult {
  rowIndex: number;
  status:   'ok' | 'rejected' | 'skipped';
  rowId:    number | null;
  errors:   ImportRowError[];
}

export interface ImportEngineProps {
  session:           AuthSession;
  initialTableName?: string;
  lockTable?:        boolean;
  schemaColumns?:    ImportColumnSchema[];
  validators?:       Record<string, (v: unknown, row: Record<string, unknown>) => string | null>;
  onComplete?:       (results: ImportResult[]) => void;
}

// ---------------------------------------------------------------------------
// Data type validators
// ---------------------------------------------------------------------------

const TYPE_CHECKS: Record<ImportDataType, (v: string) => boolean> = {
  string:  ()  => true,
  number:  (v) => !isNaN(Number(v)) && v.trim() !== '',
  date:    (v) => !isNaN(Date.parse(v)),
  boolean: (v) => ['true','false','yes','no','1','0','y','n'].includes(v.toLowerCase()),
};

// ---------------------------------------------------------------------------
// Excel helper (shared with original BulkImportPage)
// ---------------------------------------------------------------------------

function cellToString(value: ExcelJS.CellValue): string {
  if (value == null) return '';
  if (typeof value === 'string')  return value;
  if (typeof value === 'number')  return String(value);
  if (typeof value === 'boolean') return value ? 'TRUE' : 'FALSE';
  if (value instanceof Date) {
    const iso = value.toISOString();
    return iso.endsWith('T00:00:00.000Z') ? iso.slice(0, 10) : iso;
  }
  if (typeof value === 'object' && 'richText' in value && Array.isArray((value as { richText: { text?: string }[] }).richText))
    return (value as { richText: { text?: string }[] }).richText.map((r) => r.text ?? '').join('');
  if (typeof value === 'object' && 'result' in value)
    return cellToString((value as { result: ExcelJS.CellValue }).result);
  if (typeof value === 'object' && 'text' in value && typeof (value as { text?: unknown }).text === 'string')
    return (value as { text: string }).text;
  if (typeof value === 'object' && 'error' in value) return '';
  try { return String(value); } catch { return ''; }
}

function readExcelSheet(sheet: ExcelJS.Worksheet): { headers: string[]; rows: Record<string, string>[] } {
  let headerRowNum = -1;
  let headers: string[] = [];
  sheet.eachRow({ includeEmpty: false }, (row, n) => {
    if (headerRowNum !== -1) return;
    const vals = Array.isArray(row.values) ? row.values.slice(1) : [];
    if (vals.some(v => v != null && v !== '')) {
      headers = vals.map((v, i) => cellToString(v as ExcelJS.CellValue).trim() || `col_${i + 1}`);
      headerRowNum = n;
    }
  });
  if (headerRowNum === -1) return { headers: [], rows: [] };
  const rows: Record<string, string>[] = [];
  sheet.eachRow({ includeEmpty: false }, (row, n) => {
    if (n <= headerRowNum) return;
    const vals = Array.isArray(row.values) ? row.values.slice(1) : [];
    if (vals.every(v => v == null || v === '')) return;
    const obj: Record<string, string> = {};
    headers.forEach((h, i) => { obj[h] = cellToString(vals[i] as ExcelJS.CellValue); });
    rows.push(obj);
  });
  return { headers, rows };
}

// ---------------------------------------------------------------------------
// Client-side validation
// ---------------------------------------------------------------------------

function clientValidateRow(
  row:        Record<string, unknown>,
  schema:     ImportColumnSchema[],
  validators: Record<string, (v: unknown, row: Record<string, unknown>) => string | null>,
  rowIndex:   number,
): ImportRowError[] {
  const errors: ImportRowError[] = [];
  for (const col of schema) {
    const val = row[col.key];
    const str = val == null ? '' : String(val);

    if (col.required && (val == null || str.trim() === '')) {
      errors.push({ rowIndex, field: col.key, code: 'REQUIRED', message: `"${col.label}" is required.`, source: 'client' });
      continue;
    }
    if (str && col.dataType && !TYPE_CHECKS[col.dataType](str)) {
      errors.push({ rowIndex, field: col.key, code: 'INVALID_TYPE', message: `"${col.label}" must be a valid ${col.dataType}.`, source: 'client' });
    }
    if (str && col.maxLength && str.length > col.maxLength) {
      errors.push({ rowIndex, field: col.key, code: 'MAX_LENGTH', message: `"${col.label}" exceeds ${col.maxLength} characters.`, source: 'client' });
    }
    // Custom validator (passed as prop OR embedded in schema)
    const customFn = validators[col.key] ?? col.validate;
    if (customFn) {
      const msg = customFn(val, row);
      if (msg) errors.push({ rowIndex, field: col.key, code: 'CUSTOM', message: msg, source: 'client' });
    }
  }
  return errors;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const SKIP = '__skip__';

export function ImportEngine({
  session, initialTableName, lockTable, schemaColumns, validators = {}, onComplete,
}: ImportEngineProps) {
  const { t } = useTranslation();

  // ── Meta ──────────────────────────────────────────────────────────────────
  const [metaMap, setMetaMap] = useState<Record<string, CommonRoutineTableMeta>>({});
  const [metaErr, setMetaErr] = useState<string | null>(null);

  useEffect(() => {
    if (schemaColumns) return;  // explicit schema — no meta needed for columns
    loadCommonRoutineMeta()
      .then(setMetaMap)
      .catch(e => setMetaErr((e as Error).message));
  }, [schemaColumns]);

  // ── Step 1 — table selection ──────────────────────────────────────────────
  const [tableName,  setTableName]  = useState<string | null>(initialTableName ?? null);
  const [onMissing,  setOnMissing]  = useState<OnMissingLookup>('R');

  const tableOptions: MultiSelectOption[] = useMemo(
    () => Object.values(metaMap).map(m => ({
      internal_code: m.table_name,
      display_text:  m.table_name,
      description:   `Required: ${m.required_columns.join(', ') || '(none)'}`,
    })),
    [metaMap],
  );

  const targetMeta = tableName ? metaMap[tableName] : null;

  // Resolve schema columns: explicit prop > meta-derived
  const resolvedSchema = useMemo<ImportColumnSchema[]>(() => {
    if (schemaColumns) return schemaColumns;
    if (!targetMeta)   return [];
    const keys = new Set<string>([
      ...targetMeta.required_columns,
      ...Object.keys(targetMeta.lookup_resolvers),
    ]);
    return [...keys].map(k => ({
      key:      k,
      label:    k,
      required: targetMeta.required_columns.includes(k),
    }));
  }, [schemaColumns, targetMeta]);

  const apiFields = resolvedSchema.map(c => c.key);
  // Stable signal for effects: apiFields is a fresh array every render, so we key
  // dependent effects on its joined contents instead of its (always-new) identity.
  const apiFieldsKey = apiFields.join(',');

  // ── Step 2 — file upload ──────────────────────────────────────────────────
  const [parsedFile, setParsedFile] = useState<{ name: string; headers: string[]; rows: Record<string, string>[] } | null>(null);
  const [fileErr,    setFileErr]    = useState<string | null>(null);

  const handleFile = useCallback(async (f: File | null) => {
    setFileErr(null);
    setParsedFile(null);
    if (!f) return;
    try {
      const lower = f.name.toLowerCase();
      if (lower.endsWith('.csv')) {
        const text = await f.text();
        const { data, errors, meta } = Papa.parse<Record<string, string>>(text, { header: true, skipEmptyLines: true });
        if (errors.length) throw new Error(errors[0]?.message ?? 'CSV parse error');
        setParsedFile({ name: f.name, headers: meta.fields ?? [], rows: data });
      } else if (lower.endsWith('.xlsx')) {
        const buf = await f.arrayBuffer();
        const wb  = new ExcelJS.Workbook();
        await wb.xlsx.load(buf);
        const sheet = wb.worksheets[0];
        if (!sheet) throw new Error('Workbook contains no sheets.');
        const { headers, rows } = readExcelSheet(sheet);
        setParsedFile({ name: f.name, headers, rows });
      } else if (lower.endsWith('.xls')) {
        throw new Error('Legacy .xls not supported — re-save as .xlsx or .csv.');
      } else {
        throw new Error('Only .csv and .xlsx files are supported.');
      }
    } catch (e) { setFileErr((e as Error).message); }
  }, []);

  // ── Step 3 — column mapping ───────────────────────────────────────────────
  const [mapping, setMapping] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!parsedFile || apiFields.length === 0) return;
    const auto: Record<string, string> = {};
    for (const h of parsedFile.headers) {
      const match = apiFields.find(f => f.toLowerCase() === h.toLowerCase());
      auto[h] = match ?? SKIP;
    }
    setMapping(auto);
    // apiFields is read in the body but churns identity each render; apiFieldsKey is
    // the stable content signal we actually depend on (extracted above).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsedFile, apiFieldsKey]);

  // ── Step 4 — commit ───────────────────────────────────────────────────────
  const [results,   setResults]   = useState<ImportResult[] | null>(null);
  const [committed, setCommitted] = useState(false);
  const [busy,      setBusy]      = useState(false);
  const [progress,  setProgress]  = useState(0);
  const [runErr,    setRunErr]    = useState<string | null>(null);

  // Reset results on any upstream change
  const mappingKey = JSON.stringify(mapping);
  useEffect(() => { setResults(null); setCommitted(false); setProgress(0); },
    [tableName, parsedFile, mappingKey, onMissing]);

  const buildPayload = useCallback((fileRow: Record<string, string>) => {
    const row: Record<string, unknown> = {};
    for (const [col, field] of Object.entries(mapping)) {
      if (!field || field === SKIP) continue;
      const v = fileRow[col];
      if (v !== undefined && v !== '') row[field] = v;
    }
    return row;
  }, [mapping]);

  const runCommit = useCallback(async () => {
    if (!parsedFile || (!tableName && !schemaColumns)) return;
    setBusy(true); setRunErr(null);

    const actor   = getForensicActorId(session);
    const total   = parsedFile.rows.length;
    const out: ImportResult[] = [];

    try {
      for (let i = 0; i < total; i++) {
        setProgress(Math.round((i / total) * 100));
        const fileRow = parsedFile.rows[i];
        if (!fileRow) continue;
        const payload = buildPayload(fileRow);

        // Client-side pre-flight
        const clientErrs = clientValidateRow(payload, resolvedSchema, validators, i + 1);
        if (clientErrs.length > 0) {
          out.push({ rowIndex: i + 1, status: 'rejected', rowId: null, errors: clientErrs });
          continue;
        }

        // Server commit via Common Routine
        const res: CommonRoutineResult = await executeCommonRoutine({
          table_name:        tableName ?? schemaColumns?.[0]?.key ?? '', // fallback not reached
          operation:         'ADD',
          row:               payload,
          on_missing_lookup: onMissing,
          actor_user_id:     actor,
          session_token:     session.session_token,
        });

        const serverErrs: ImportRowError[] = (res.errors ?? []).map(e => ({
          rowIndex: i + 1,
          field:    e.field,
          code:     e.code,
          message:  e.message,
          source:   'server' as const,
        }));

        out.push({
          rowIndex: i + 1,
          status:   res.result_code === 'OK' ? 'ok' : 'rejected',
          rowId:    res.row_id,
          errors:   serverErrs,
        });
      }
      setProgress(100);
      setResults(out);
      setCommitted(true);
      onComplete?.(out);
    } catch (e) {
      setRunErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }, [parsedFile, tableName, schemaColumns, onMissing, session, buildPayload, resolvedSchema, validators, onComplete]);

  // ── Summary ───────────────────────────────────────────────────────────────
  const summary = useMemo(() => {
    if (!results) return null;
    const ok      = results.filter(r => r.status === 'ok').length;
    const failed  = results.filter(r => r.status === 'rejected').length;
    const skipped = results.filter(r => r.status === 'skipped').length;
    return { ok, failed, skipped, total: results.length };
  }, [results]);

  const allErrors = useMemo(
    () => results?.flatMap(r => r.errors) ?? [],
    [results],
  );

  const ready = !!(parsedFile && (tableName || schemaColumns) && apiFields.length > 0);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex h-10 shrink-0 items-center gap-3 border-b border-csm-border bg-white px-3">
        <h1 className="text-csm-md font-semibold uppercase tracking-wider text-csm-ink">
          {t('bulk_import.title')}
        </h1>
        {busy && (
          <span className="text-csm-xs text-csm-muted">
            {t('bulk_import.running')} {progress}%
          </span>
        )}
        {(metaErr || fileErr || runErr) && (
          <span className="text-csm-xs text-csm-danger">
            {metaErr ?? fileErr ?? runErr}
          </span>
        )}
      </div>

      <div className="flex-1 overflow-auto p-4 space-y-4">

        {/* ── Step 1: Target table ──────────────────────────────────────── */}
        <section className="rounded border border-csm-border bg-white p-4">
          <h2 className="mb-3 text-csm-xs font-semibold uppercase tracking-wider text-csm-muted">
            {t('bulk_import.step_pick_target')}
          </h2>

          {!schemaColumns && !lockTable && (
            <div className="mb-3">
              <FilteredModalSingleSelect
                label={t('bulk_import.target_table')}
                options={tableOptions}
                selectedCode={tableName}
                onChange={setTableName}
                allowClear={false}
                placeholder={t('common.select_placeholder')}
              />
            </div>
          )}

          {(lockTable || schemaColumns) && tableName && (
            <p className="mb-2 text-csm-sm text-csm-ink">
              Table: <span className="font-mono font-semibold">{tableName}</span>
            </p>
          )}

          <div>
            <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">
              {t('bulk_import.on_missing_lookup')}
            </label>
            <div className="flex gap-4 text-csm-sm">
              {(['R', 'C'] as OnMissingLookup[]).map(v => (
                <label key={v} className="flex items-center gap-1.5 cursor-pointer">
                  <input type="radio" checked={onMissing === v}
                    onChange={() => setOnMissing(v)} className="accent-primary" />
                  {v === 'R' ? t('bulk_import.on_missing_reject') : t('bulk_import.on_missing_create')}
                </label>
              ))}
            </div>
          </div>
        </section>

        {/* ── Step 2: File upload ───────────────────────────────────────── */}
        {(tableName || schemaColumns) && (
          <section className="rounded border border-csm-border bg-white p-4">
            <h2 className="mb-3 text-csm-xs font-semibold uppercase tracking-wider text-csm-muted">
              {t('bulk_import.step_upload')}
            </h2>
            <input
              type="file"
              accept=".csv,.xlsx"
              onChange={e => void handleFile(e.target.files?.[0] ?? null)}
              className="text-csm-sm"
            />
            {parsedFile ? (
              <p className="mt-2 text-csm-xs text-csm-muted">
                {t('bulk_import.headers_detected', {
                  count: parsedFile.headers.length,
                  rows:  parsedFile.rows.length,
                })}
              </p>
            ) : (
              <p className="mt-2 text-csm-xs italic text-csm-muted">{t('bulk_import.no_file')}</p>
            )}
          </section>
        )}

        {/* ── Step 3: Column mapping ────────────────────────────────────── */}
        {parsedFile && apiFields.length > 0 && (
          <section className="rounded border border-csm-border bg-white p-4">
            <h2 className="mb-3 text-csm-xs font-semibold uppercase tracking-wider text-csm-muted">
              {t('bulk_import.step_map')}
            </h2>
            <div className="overflow-auto">
              <table className="min-w-full border-collapse text-csm-sm">
                <thead>
                  <tr className="bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-muted">
                    <th className="border-b border-csm-border px-2 py-1 text-left w-1/2">
                      {t('bulk_import.column_in_file')}
                    </th>
                    <th className="border-b border-csm-border px-2 py-1 text-left w-1/2">
                      {t('bulk_import.maps_to_api_field')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {parsedFile.headers.map(h => (
                    <tr key={h}>
                      <td className="border-b border-csm-border-soft px-2 py-1 font-mono">{h}</td>
                      <td className="border-b border-csm-border-soft px-2 py-1">
                        <select
                          value={mapping[h] ?? SKIP}
                          onChange={e => setMapping(p => ({ ...p, [h]: e.target.value }))}
                          className="h-7 w-full rounded border border-csm-border px-1
                                     text-csm-sm focus:border-primary focus:outline-none"
                        >
                          <option value={SKIP}>{t('bulk_import.skip')}</option>
                          {apiFields.map(f => {
                            const col = resolvedSchema.find(c => c.key === f);
                            return (
                              <option key={f} value={f}>
                                {col?.label ?? f}{col?.required ? ' *' : ''}
                              </option>
                            );
                          })}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* ── Step 4: Commit ────────────────────────────────────────────── */}
        {ready && (
          <section className="rounded border border-csm-border bg-white p-4">
            <h2 className="mb-3 text-csm-xs font-semibold uppercase tracking-wider text-csm-muted">
              {t('bulk_import.step_review')}
            </h2>

            {/* Progress bar */}
            {busy && (
              <div className="mb-3">
                <div className="h-2 rounded bg-csm-border-soft">
                  <div
                    className="h-2 rounded bg-primary transition-all"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <p className="mt-1 text-csm-xs text-csm-muted text-right">{progress}%</p>
              </div>
            )}

            <div className="flex gap-2">
              <button
                type="button"
                onClick={runCommit}
                disabled={busy || committed}
                className="rounded border border-primary bg-primary px-4 py-1.5
                           text-csm-sm text-white hover:bg-primary-hover disabled:opacity-60"
              >
                {busy ? t('bulk_import.running') : t('bulk_import.commit')}
              </button>
              {committed && (
                <button
                  type="button"
                  onClick={() => {
                    setResults(null); setCommitted(false);
                    setParsedFile(null); setMapping({});
                  }}
                  className="rounded border border-csm-border bg-white px-4 py-1.5
                             text-csm-sm text-csm-ink-soft hover:bg-csm-surface-alt"
                >
                  {t('import.import_another')}
                </button>
              )}
            </div>

            {/* Summary banner */}
            {summary && (
              <div className="mt-3 rounded border border-csm-border bg-csm-surface-alt px-3 py-2 text-csm-sm">
                {t('bulk_import.result_summary', summary)}
                {summary.failed > 0 && (
                  <span className="ml-2 font-semibold text-csm-danger">
                    — {t('import.see_errors_below')}
                  </span>
                )}
              </div>
            )}
          </section>
        )}

        {/* ── Step 5: Error detail grid ─────────────────────────────────── */}
        {allErrors.length > 0 && (
          <section className="rounded border border-csm-danger-border bg-white p-4">
            <h2 className="mb-3 text-csm-xs font-semibold uppercase tracking-wider text-csm-danger">
              {t('import.validation_errors')} ({allErrors.length})
            </h2>
            <div className="overflow-auto">
              <table className="min-w-full border-collapse text-csm-sm">
                <thead>
                  <tr className="bg-csm-danger-soft text-csm-xs uppercase tracking-wider text-csm-danger">
                    <th className="border-b border-csm-danger-border px-2 py-1 text-left w-12">
                      {t('import.error_row')}
                    </th>
                    <th className="border-b border-csm-danger-border px-2 py-1 text-left w-32">
                      {t('import.error_field')}
                    </th>
                    <th className="border-b border-csm-danger-border px-2 py-1 text-left w-32">
                      {t('import.error_code')}
                    </th>
                    <th className="border-b border-csm-danger-border px-2 py-1 text-left">
                      {t('import.error_message')}
                    </th>
                    <th className="border-b border-csm-danger-border px-2 py-1 text-left w-16">
                      {t('import.error_source')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {allErrors.map((e, i) => (
                    <tr key={i} className="border-b border-csm-border-soft">
                      <td className="px-2 py-1 font-mono text-csm-xs">{e.rowIndex}</td>
                      <td className="px-2 py-1 font-mono text-csm-xs">{e.field}</td>
                      <td className="px-2 py-1 font-mono text-csm-xs text-csm-muted">{e.code}</td>
                      <td className="px-2 py-1 text-csm-danger">{e.message}</td>
                      <td className="px-2 py-1">
                        <span className={`rounded border px-1 text-csm-2xs uppercase ${
                          e.source === 'client'
                            ? 'border-csm-warning-border bg-csm-warning-soft text-csm-warning'
                            : 'border-csm-danger-border bg-csm-danger-soft text-csm-danger'
                        }`}>
                          {e.source}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
